/**
 * PositionsPanel — open positions table aggregated across all agents.
 */

import { useAgentsOverview, useAgentPositions } from "@/sdk/hooks/use-agents";

export function PositionsPanel() {
  const { data: overview, isError, isPending } = useAgentsOverview();
  const agentIds = overview?.agents ? Object.keys(overview.agents) : [];

  // Fetch positions for all agents
  // For simplicity, we aggregate in the component; a dedicated endpoint would be better
  return (
    <div className="flex h-full flex-col bg-zinc-950">
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-zinc-950">
            <tr className="border-b border-zinc-800 text-left text-zinc-500">
              <th className="px-3 py-1.5 font-medium">Agent</th>
              <th className="px-3 py-1.5 font-medium">Market</th>
              <th className="px-3 py-1.5 font-medium">Side</th>
              <th className="px-3 py-1.5 font-medium text-right">Size</th>
              <th className="px-3 py-1.5 font-medium text-right">Entry</th>
              <th className="px-3 py-1.5 font-medium text-right">Now</th>
              <th className="px-3 py-1.5 font-medium text-right">P&L</th>
              <th className="px-3 py-1.5 font-medium">Strategy</th>
            </tr>
          </thead>
          <tbody>
            {agentIds.map((id) => (
              <PositionRows key={id} agentId={id} />
            ))}
          </tbody>
        </table>
        {/* "No agents loaded" must mean the registry is genuinely empty; a
            failed read is a different fact and says so. No flash of either
            state during the initial load (isPending). */}
        {agentIds.length === 0 &&
          !isPending &&
          (isError ? (
            <div className="mt-8 text-center text-amber-400">
              Agent data unavailable — backend unreachable
            </div>
          ) : (
            <div className="mt-8 text-center text-zinc-600">No agents loaded</div>
          ))}
      </div>
    </div>
  );
}

function PositionRows({ agentId }: { agentId: string }) {
  const { data } = useAgentPositions(agentId, "open");
  const positions = data?.positions ?? [];

  if (positions.length === 0) return null;

  return (
    <>
      {positions.map((p) => (
        <tr
          key={p.id}
          className="border-b border-zinc-800/30 hover:bg-zinc-900/50"
        >
          <td className="px-3 py-1.5 text-zinc-400">{agentId}</td>
          <td className="max-w-[200px] truncate px-3 py-1.5 text-zinc-300">
            {p.question}
          </td>
          <td className="px-3 py-1.5">
            <span className={`font-bold ${p.side === "BUY" ? "text-emerald-400" : "text-red-400"}`}>
              {p.side}
            </span>
          </td>
          <td className="px-3 py-1.5 text-right font-mono text-zinc-300">
            ${(p.size ?? 0).toFixed(0)}
          </td>
          <td className="px-3 py-1.5 text-right font-mono text-zinc-300">
            {(p.entry_price ?? 0).toFixed(4)}
          </td>
          <td className="px-3 py-1.5 text-right font-mono text-zinc-300">
            {p.current_price != null
              ? Number(p.current_price).toFixed(4)
              : "—"}
          </td>
          <td className="px-3 py-1.5 text-right font-mono">
            <span className={(p.unrealized_pnl ?? p.realized_pnl ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}>
              {(p.unrealized_pnl ?? p.realized_pnl ?? 0) >= 0 ? "+" : ""}
              ${(p.unrealized_pnl ?? p.realized_pnl ?? 0).toFixed(2)}
            </span>
          </td>
          <td className="px-3 py-1.5">
            <span className="rounded bg-zinc-800 px-1 py-0.5 text-[10px] text-zinc-400">
              {p.strategy}
            </span>
          </td>
        </tr>
      ))}
    </>
  );
}
